import React, { FC } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Button,
  Column,
  Grid,
  HorizontalLine,
  Stack,
  Text,
  useBreakpoint,
  UserCard,
} from '@components'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersOrganizersSection_guild$key } from './__generated__/GuildMembersOrganizersSection_guild.graphql'

export const GuildMembersOrganizersSection: FC<{
  guild: GuildMembersOrganizersSection_guild$key
}> = ({ guild }) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersOrganizersSection_guild on Guild
      @refetchable(queryName: "GuildMembersOrganizersSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 3 }
        cursor: { type: "Cursor" }
      ) {
        organizers(first: $count, after: $cursor)
          @connection(key: "GuildMembersOrganizersSection_guild_organizers") {
          edges {
            node {
              ...UserCard_user
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const breakpoint = useBreakpoint()

  if (data.organizers.edges.length === 0) {
    return null
  }

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        <Stack direction='horizontal' gap={4} alignItems='center'>
          <Text size={4} variant='cardHeading'>
            Organizers
          </Text>

          <HorizontalLine variant='cardHeading' />
        </Stack>

        {data.organizers.edges.map((edge) => (
          <UserCard
            key={edge.cursor}
            user={edge.node}
            limitDescriptionLines={6}
          />
        ))}

        {hasNext && (
          <Button
            variant='secondary'
            hoverVariant='secondaryHover'
            onPress={() => {
              if (isLoadingNext) {
                return
              }

              loadNext(3)
            }}
          >
            <Text variant='buttonSecondary' size={4}>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Text>
          </Button>
        )}
      </Stack>
    )
  }

  const organizerGroups = data.organizers.edges.reduce<
    Array<Array<(typeof data.organizers.edges)[number]>>
  >(
    (memo, organizer, index) => {
      if (index % INITIAL_COUNTS[breakpoint] === 0) {
        memo.push([organizer])
      } else {
        memo[memo.length - 1].push(organizer)
      }

      return memo
    },
    []
  )

  return (
    <Stack gap={4}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Organizers
        </Text>

        <HorizontalLine variant='cardHeading' />
      </Stack>

      <Stack gap={4}>
        {organizerGroups.map((group, index) => {
          return (
            <Stack key={index}>
              <Grid gap={4}>
                {group.map((edge) => (
                  <Column key={edge.cursor}>
                    <UserCard
                      user={edge.node}
                      limitDescriptionLines={6}
                      expand
                    />
                  </Column>
                ))}

                {INITIAL_COUNTS[breakpoint] - group.length > 0 ? (
                  <Column span={INITIAL_COUNTS[breakpoint] - group.length} />
                ) : null}
              </Grid>
            </Stack>
          )
        })}
      </Stack>

      {hasNext ? (
        <Button
          variant='secondary'
          hoverVariant='secondaryHover'
          onPress={() => {
            if (isLoadingNext) {
              return
            }

            loadNext(3)
          }}
        >
          <Text variant='buttonSecondary' size={4}>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Text>
        </Button>
      ) : null}
    </Stack>
  )
}
